import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const DEVICE_ID_KEY = 'depth_did'
const ENDPOINT = '/_t/event'

export type TrackEvent =
  | 'pageview'
  | 'app_open'
  | 'heartbeat'
  | 'feature_use'
  | 'error'
  | 'download'

interface TrackPayload {
  event_type: TrackEvent
  device_id: string
  app_version: string
  os: string
  path: string
  referrer: string
  duration_ms?: number
  extra?: Record<string, unknown>
}

function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(DEVICE_ID_KEY, id)
    }
    return id
  } catch {
    // Private mode or Safari ITP can throw; fall back to per-session id.
    return 'ephemeral-' + Math.random().toString(36).slice(2)
  }
}

function detectBrowser(): string {
  const ua = navigator.userAgent
  if (/Edg\//.test(ua)) return 'edge'
  if (/Chrome\//.test(ua)) return 'chrome'
  if (/Firefox\//.test(ua)) return 'firefox'
  if (/Safari\//.test(ua)) return 'safari'
  return 'other'
}

export function track(
  event_type: TrackEvent,
  extra: Record<string, unknown> = {},
): void {
  const payload: TrackPayload = {
    event_type,
    device_id: getDeviceId(),
    app_version: __APP_VERSION__,
    os: `web-${detectBrowser()}`,
    path: location.pathname,
    referrer: document.referrer,
    extra,
  }

  const body = JSON.stringify(payload)
  const blob = new Blob([body], { type: 'application/json' })

  // sendBeacon survives page unload and is fire-and-forget. Falls back to
  // fetch keepalive for the rare browser without it (or when the queue is full).
  if (navigator.sendBeacon?.(ENDPOINT, blob)) return
  fetch(ENDPOINT, { method: 'POST', body, keepalive: true }).catch(() => {})
}

export function useRouteTracking(): void {
  const location = useLocation()
  useEffect(() => {
    track('pageview')
  }, [location.pathname])
}
